import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useSidebar } from '../contexts/SidebarContext';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { apiService } from '../services/api';
import { formatDateShort, formatRelativeTime } from '../utils/dateFormat';

const Dashboard: React.FC = () => {
  const { isCollapsed } = useSidebar();
  const { user } = useAuth();
  const { darkMode } = useTheme();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const filterType = searchParams.get('filter') || 'my_task';
  const searchQuery = searchParams.get('search') || '';
  
  const [tasks, setTasks] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState({
    totalRequests: 0,
    newCount: 0,
    draftingCount: 0,
    reviewCount: 0,
    onProgressCount: 0,
    sendToCustomerCount: 0,
    completedCount: 0
  });

  // Load data saat component mount, atau filter/search berubah
  useEffect(() => {
    loadDashboardData();
  }, [filterType, searchQuery]);

  const loadDashboardData = async () => {
    setLoading(true);
    try {
      // Determine which tasks to load based on filterType
      let tasksToLoad: any[] = [];
      
      if (filterType === 'my_task') {
        // My Task: issued_by_me OR assigned_to_me OR assigned via task_assignments
        const [myTasksData, assignedTasksData, assignedTasksFromTable] = await Promise.all([
          apiService.getTasks({
            is_active: 1,
            filter_type: 'issued_by_me',
            ...(searchQuery.trim() && { search: searchQuery.trim() })
          }),
          apiService.getTasks({
            is_active: 1,
            filter_type: 'assigned_to_me',
            ...(searchQuery.trim() && { search: searchQuery.trim() })
          }),
          apiService.getMyAssignedTasks({
            is_active: 1,
            limit: 1000
          })
        ]);
        
        // Combine and remove duplicates
        const taskMap = new Map();
        [...myTasksData, ...assignedTasksData, ...assignedTasksFromTable].forEach((task: any) => {
          taskMap.set(task.id, task);
        });
        tasksToLoad = Array.from(taskMap.values());
        
        // Apply search filter if provided (client-side filtering for task_assignments)
        if (searchQuery.trim()) {
          const searchLower = searchQuery.trim().toLowerCase();
          tasksToLoad = tasksToLoad.filter((task: any) => 
            task.title?.toLowerCase().includes(searchLower) ||
            task.description?.toLowerCase().includes(searchLower) ||
            task.company?.name?.toLowerCase().includes(searchLower) ||
            task.agreement_type?.name?.toLowerCase().includes(searchLower)
          );
        }
      } else if (filterType === 'my_team') {
        // My Team: tasks from users in the same team
        const teamTasksData = await apiService.getTasks({
          is_active: 1,
          filter_type: 'my_team',
          ...(searchQuery.trim() && { search: searchQuery.trim() })
        });
        tasksToLoad = teamTasksData;
      } else if (filterType === 'assigned_tasks') {
        // Only tasks assigned via task_assignments table
        const assignedTasksFromTable = await apiService.getMyAssignedTasks({
          is_active: 1,
          limit: 1000
        });
        tasksToLoad = assignedTasksFromTable;
        
        // Apply search filter if provided (client-side filtering)
        if (searchQuery.trim()) {
          const searchLower = searchQuery.trim().toLowerCase();
          tasksToLoad = tasksToLoad.filter((task: any) => 
            task.title?.toLowerCase().includes(searchLower) ||
            task.description?.toLowerCase().includes(searchLower) ||
            task.company?.name?.toLowerCase().includes(searchLower) ||
            task.agreement_type?.name?.toLowerCase().includes(searchLower)
          );
        }
      }
      
      setTasks(tasksToLoad);

      // Load all tasks for stats calculation
      const allTasksData = await apiService.getTasks({
        is_active: 1,
        limit: 1000
      });

      // 1. Biru
      const totalRequests = allTasksData.length;
      const newCount = allTasksData.filter((task: any) => task.status?.name?.toUpperCase() === 'NEW').length;
      const draftingCount = allTasksData.filter((task: any) => task.status?.name?.toUpperCase() === 'DRAFTING').length;
      
      // 2. Ungu
      const reviewCount = allTasksData.filter((task: any) => task.status?.name?.toUpperCase() === 'REVIEW').length;
      const onProgressCount = draftingCount + reviewCount;

      // 4. Hijau
      const completedCount = allTasksData.filter((task: any) => {
        const statusName = task.status?.name?.toUpperCase() || '';
        return statusName === 'APPROVED' || statusName === 'RELEASED';
      }).length;

      setStats({
        totalRequests,
        newCount,
        draftingCount,
        reviewCount,
        onProgressCount,
        sendToCustomerCount: reviewCount,
        completedCount
      });
    } catch (err: any) {
      console.error('Error loading dashboard data:', err);
    } finally {
      setLoading(false);
    }
  };

  const getAgreementTypeIcon = (agreementType: any) => {
    if (!agreementType) return 'fa-file';
    const name = agreementType.name?.toUpperCase() || '';
    if (name.includes('PKS') || name.includes('PARTNERSHIP')) return 'fa-handshake';
    if (name.includes('PO') || name.includes('PURCHASE')) return 'fa-file-invoice';
    if (name.includes('BAST')) return 'fa-file-alt';
    if (name.includes('MOU')) return 'fa-file-signature';
    if (name.includes('INVOICE')) return 'fa-file-invoice';
    return 'fa-file';
  };

  const getStatusColors = (status: any) => {
    if (!status) return { bg: 'bg-gray-100', text: 'text-gray-700' };
    const name = status.name?.toUpperCase() || '';
    if (name.includes('DRAFTING')) return { bg: 'bg-yellow-100', text: 'text-yellow-700' };
    if (name.includes('BUSINESS REVIEW')) return { bg: 'bg-orange-100', text: 'text-orange-700' };
    if (name.includes('LEGAL REVIEW')) return { bg: 'bg-teal-100', text: 'text-teal-700' };
    if (name.includes('APPROVED')) return { bg: 'bg-green-100', text: 'text-green-700' };
    return { bg: 'bg-gray-100', text: 'text-gray-700' };
  };

  const getAgreementTypeClass = (agreementType: any) => {
    if (!agreementType) return 'bg-gray-100 text-gray-700';
    const name = agreementType.name?.toUpperCase() || '';
    if (name.includes('PKS')) return 'bg-purple-100 text-purple-700';
    if (name.includes('MOU')) return 'bg-indigo-100 text-indigo-700';
    if (name.includes('PO')) return 'bg-violet-100 text-violet-700';
    if (name.includes('BAST')) return 'bg-green-100 text-green-700';
    if (name.includes('INVOICE')) return 'bg-violet-100 text-violet-700';
    return 'bg-gray-100 text-gray-700';
  };


  return (
    <>
      <section 
        className={`${darkMode ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-200'} border-b px-6 py-4 fixed z-30 transition-all duration-300`}
        style={{ 
          left: isCollapsed ? '64px' : '256px',
          right: 0,
          top: '56px'
        }}
      >
        <div className="flex items-center justify-between">
          <div>
            <h1 className={`text-2xl font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>My Work</h1>
            <p className={`text-sm mt-1 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              Personal dashboard for {user?.name || 'User'}
              {user?.department && ` · ${user.department}`}
            </p>
          </div>
          <div className="flex items-center space-x-3">
          </div>
        </div>
      </section>

      <section className="px-6 py-6" style={{ marginTop: '100px' }}>
        <div className="grid grid-cols-4 gap-4">
          <div className={`${darkMode ? 'bg-gradient-to-br from-blue-900/50 to-blue-800/50 border-blue-700' : 'bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200'} border rounded-lg p-5 transition-colors duration-300`}>
            <div className="flex items-center justify-between mb-3">
              <div className="w-10 h-10 bg-blue-500 rounded-lg flex items-center justify-center">
                <i className="fa-solid fa-inbox text-white text-lg"></i>
              </div>
              <span className="px-2 py-1 text-xs font-bold bg-blue-500 text-white rounded">REQUESTS</span>
            </div>
            <h3 className={`text-2xl font-bold mb-1 ${darkMode ? 'text-blue-200' : 'text-blue-900'}`}>{stats.totalRequests}</h3>
            <p className={`text-sm font-medium ${darkMode ? 'text-blue-300' : 'text-blue-800'}`}>Total Permohonan</p>
            <div className={`text-xs mt-2 space-y-1 ${darkMode ? 'text-blue-400' : 'text-blue-700'}`}>
              <div className="flex justify-between"><span>New Request:</span> <span className="font-bold">{stats.newCount}</span></div>
              <div className="flex justify-between"><span>Belum Dikerjakan:</span> <span className="font-bold">{stats.newCount}</span></div>
              <div className="flex justify-between"><span>Sedang Dikerjakan:</span> <span className="font-bold">{stats.draftingCount}</span></div>
            </div>
          </div>

          <div className={`${darkMode ? 'bg-gradient-to-br from-purple-900/50 to-purple-800/50 border-purple-700' : 'bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200'} border rounded-lg p-5 transition-colors duration-300`}>
            <div className="flex items-center justify-between mb-3">
              <div className="w-10 h-10 bg-purple-500 rounded-lg flex items-center justify-center">
                <i className="fa-solid fa-spinner text-white text-lg"></i>
              </div>
              <span className="px-2 py-1 text-xs font-bold bg-purple-500 text-white rounded">PROGRESS</span>
            </div>
            <h3 className={`text-2xl font-bold mb-1 ${darkMode ? 'text-purple-200' : 'text-purple-900'}`}>{stats.onProgressCount}</h3>
            <p className={`text-sm font-medium ${darkMode ? 'text-purple-300' : 'text-purple-800'}`}>Sedang Dikerjakan</p>
            <p className={`text-xs mt-1 ${darkMode ? 'text-purple-400' : 'text-purple-700'}`}>Drafting dan Review User</p>
          </div>

          <div className={`${darkMode ? 'bg-gradient-to-br from-orange-900/50 to-orange-800/50 border-orange-700' : 'bg-gradient-to-br from-orange-50 to-orange-100 border-orange-200'} border rounded-lg p-5 transition-colors duration-300`}>
            <div className="flex items-center justify-between mb-3">
              <div className="w-10 h-10 bg-orange-500 rounded-lg flex items-center justify-center">
                <i className="fa-solid fa-paper-plane text-white text-lg"></i>
              </div>
              <span className="px-2 py-1 text-xs font-bold bg-orange-500 text-white rounded">CUSTOMER</span>
            </div>
            <h3 className={`text-2xl font-bold mb-1 ${darkMode ? 'text-orange-200' : 'text-orange-900'}`}>{stats.sendToCustomerCount}</h3>
            <p className={`text-sm font-medium ${darkMode ? 'text-orange-300' : 'text-orange-800'}`}>Send To Customer</p>
            <p className={`text-xs mt-1 ${darkMode ? 'text-orange-400' : 'text-orange-700'}`}>Untuk direview</p>
          </div>

          <div className={`${darkMode ? 'bg-gradient-to-br from-green-900/50 to-green-800/50 border-green-700' : 'bg-gradient-to-br from-green-50 to-green-100 border-green-200'} border rounded-lg p-5 transition-colors duration-300`}>
            <div className="flex items-center justify-between mb-3">
              <div className="w-10 h-10 bg-green-500 rounded-lg flex items-center justify-center">
                <i className="fa-solid fa-check-circle text-white text-lg"></i>
              </div>
              <span className="px-2 py-1 text-xs font-bold bg-green-500 text-white rounded">COMPLETED</span>
            </div>
            <h3 className={`text-2xl font-bold mb-1 ${darkMode ? 'text-green-200' : 'text-green-900'}`}>{stats.completedCount}</h3>
            <p className={`text-sm font-medium ${darkMode ? 'text-green-300' : 'text-green-800'}`}>Approve / Final</p>
            <p className={`text-xs mt-1 ${darkMode ? 'text-green-400' : 'text-green-700'}`}>Completed dari setiap user Legal</p>
          </div>
        </div>
      </section>

      <section className="px-6 py-6">
          <div className={`${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} rounded-lg border overflow-hidden transition-colors duration-300`}>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className={`${darkMode ? 'bg-gray-900 border-gray-700' : 'bg-gray-50 border-gray-200'} border-b`}>
                  <tr>
                    <th className={`px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider w-12 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      <input type="checkbox" className={`rounded ${darkMode ? 'border-gray-600' : 'border-gray-300'}`} />
                    </th>
                    <th className={`px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>NO PID</th>
                    <th className={`px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Nama PT</th>
                    <th className={`px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Nilai Perjanjian</th>
                    <th className={`px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Jangka Waktu</th>
                    <th className={`px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Status</th>
                    <th className={`px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Aging</th>
                    <th className={`px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Actions</th>
                  </tr>
                </thead>
                <tbody className={`divide-y ${darkMode ? 'divide-gray-700' : 'divide-gray-200'}`}>
                  {loading ? (
                    <tr>
                      <td colSpan={8} className={`px-6 py-8 text-center ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        <div className="flex items-center justify-center">
                          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-violet-600 mr-3"></div>
                          Memuat data...
                        </div>
                      </td>
                    </tr>
                  ) : tasks.length === 0 ? (
                    <tr>
                      <td colSpan={8} className={`px-6 py-8 text-center ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        <i className="fa-solid fa-inbox text-2xl mb-2 block"></i>
                        Tidak ada task
                      </td>
                    </tr>
                  ) : (
                    tasks.map((task) => {
                      const statusColors = getStatusColors(task.status);
                      const agreementTypeClass = getAgreementTypeClass(task.agreement_type);
                      const iconClass = getAgreementTypeIcon(task.agreement_type);
                      
                      // Calculate days ago (aging)
                      const createdDate = new Date(task.created_at);
                      const now = new Date();
                      const diffTime = Math.abs(now.getTime() - createdDate.getTime());
                      const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
                      
                      return (
                      <tr key={task.id} className={darkMode ? 'hover:bg-gray-700 cursor-pointer' : 'hover:bg-gray-50 cursor-pointer'}>
                        <td className="px-6 py-4">
                          <input type="checkbox" className={`rounded ${darkMode ? 'border-gray-600' : 'border-gray-300'}`} />
                        </td>
                        <td className="px-6 py-4">
                          <span className={`text-sm font-mono font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                            {task.pid || task.no_pid || '-'}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <div 
                            onClick={() => navigate(`/agreement/${task.id}`)}
                            className="flex items-center space-x-3 hover:text-violet-600 cursor-pointer"
                          >
                            <div className={`w-8 h-8 ${darkMode ? 'bg-violet-900/50' : 'bg-violet-100'} rounded flex items-center justify-center flex-shrink-0`}>
                              <i className={`fa-solid ${iconClass} ${darkMode ? 'text-violet-300' : 'text-violet-600'} text-sm`}></i>
                            </div>
                            <div>
                              <div className="flex items-center space-x-2">
                                <p className={`text-sm font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                                  {task.title} {task.description ? `- ${task.description}` : ''}
                                </p>
                                {task.agreement_type?.name && (
                                  <span className={`px-1.5 py-0.5 text-[10px] font-semibold ${agreementTypeClass} rounded`}>
                                    {task.agreement_type.name}
                                  </span>
                                )}
                              </div>
                              <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                Created by {task.creator?.name || 'Unknown'} · {formatRelativeTime(task.created_at)}
                              </p>
                            </div>
                          </div>
                        </td>

                        <td className="px-6 py-4">
                          <span className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                            {task.agreement_value || '-'}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <span className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                            {task.period || '-'}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <span className={`px-2 py-1 text-xs font-medium ${statusColors.bg} ${statusColors.text} rounded`}>
                            {task.status?.name || 'N/A'}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <span className={`px-2.5 py-1 text-xs font-semibold rounded-full border inline-flex items-center ${
                            diffDays > 7 
                              ? darkMode ? 'bg-red-900/40 text-red-300 border-red-800' : 'bg-red-50 text-red-700 border-red-200'
                              : diffDays > 3
                              ? darkMode ? 'bg-orange-900/40 text-orange-300 border-orange-800' : 'bg-orange-50 text-orange-700 border-orange-200'
                              : darkMode ? 'bg-green-900/40 text-green-300 border-green-800' : 'bg-green-50 text-green-700 border-green-200'
                          }`}>
                            <i className="fa-solid fa-clock-rotate-left mr-1.5"></i>
                            {diffDays === 0 ? 'Today' : `${diffDays} Day${diffDays > 1 ? 's' : ''}`}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center space-x-2">
                            <button 
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/agreement/${task.id}`);
                              }}
                              className={`p-2 text-violet-600 rounded transition-colors ${
                                darkMode ? 'hover:bg-gray-700' : 'hover:bg-violet-50'
                              }`}
                              title="View"
                            >
                              <i className="fa-solid fa-eye text-sm"></i>
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </section>
    </>
  );
};

export default Dashboard;
